import React, { useState } from 'react';
import { FONTS } from '../../constants';
import { apiPost } from '../../api/http';
import './AiGenerator.css';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

interface AiGeneratorProps {
  onGenerate: (result: { name: string; description: string }) => void;
  onClose: () => void;
  category?: string;
  technology?: string;
}

const AiGenerator: React.FC<AiGeneratorProps> = ({
  onGenerate,
  onClose,
  category,
  technology
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDark =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  function isGibberish(str: string): boolean {
    const s = str.trim();

    if (s.length < 3) return true;

    if (!/[aeiou]/i.test(s)) return true;

    if (/^(.)\1+$/.test(s)) return true;

    const letters = s.replace(/[^a-zA-Z]/g, '');

    if (letters.length < s.length * 0.4) return true;

    return false;
  }

  const handleGenerate = async () => {
    setError('');

    const trimmed = prompt.trim();

    if (!trimmed || trimmed.length < 3 || isGibberish(trimmed)) {
      setError('Please give a valid prompt');
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        prompt,
        type: 'activity',
        maxTokens: 1500
      };

      if (category) payload.category = category;

      if (technology) payload.technology = technology;

      const data = await apiPost<any>(
        '/azureai/generate-activity',
        payload
      );

      const result = data.result || data;

      const name = result.name
        ? result.name.slice(0, 70)
        : '';

      const description = result.description
        ? result.description.slice(0, 700)
        : '';

      const fallbackNames = [
        'Input Not Recognized',
        'Invalid Input Notification',
        'Input Not Recognised',
        'Invalid Input',
        'Unrecognized Input'
      ];

      const fallbackDescriptionPatterns = [
        /clarify|re-enter|unclear|random|specific request|provide more details|assist you/i
      ];

      const isFallbackName = fallbackNames.some((fb) =>
        name.toLowerCase().includes(fb.toLowerCase())
      );

      const isFallbackDesc = fallbackDescriptionPatterns.some((re) =>
        re.test(description)
      );

      if (
        !name ||
        !description ||
        isFallbackName ||
        isFallbackDesc
      ) {
        setError('Please give a valid prompt');
        return;
      }

      onGenerate({ name, description });
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`ai-generator-container${isDark ? ' dark' : ''}`}>
      <div className="ai-generator-header">
        <span className="ai-generator-title">
          <AutoAwesomeIcon className="icon" />
          {' '}AI Generator
        </span>

        <button
          className="ai-generator-close"
          onClick={onClose}
          title="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div style={{ position: 'relative' }}>
        <textarea
          className="ai-generator-input"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);

            if (error) setError('');
          }}
          disabled={loading}
          rows={4}
          placeholder="Create a certification program for React native developers"
          style={{
            resize: 'vertical',
            minHeight: 60,
            maxHeight: 200,
            overflow: 'auto',
            fontFamily: FONTS.primary,
            width: '100%',
            boxSizing: 'border-box',
            paddingRight: 32
          }}
          autoFocus
        />

        {prompt && !loading && (
          <button
            type="button"
            aria-label="Clear prompt"
            onClick={() => setPrompt('')}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDark ? '#e0e0e0' : '#6b7280',
              zIndex: 2
            }}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>

      <button
        className="ai-generator-generate-btn"
        onClick={handleGenerate}
        disabled={loading || !prompt.trim()}
      >
        {loading ? 'Generating...' : 'Generate'}
      </button>

      {error && (
        <div className="ai-generator-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default AiGenerator;